import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import DeleteButton from '../Generic/DeleteButton';
import StorageAddForm from './StorageAddForm';
import StorageBoxEditForm from './StorageBoxEditForm';
import StorageMoveForm from './StorageMoveForm';
import StorageRenameForm from './StorageRenameForm';
import { canHaveChildren, readSlotCount } from '../../utils/storageTree';

/**
 * Remove a place, through the app's delete dialog. Everything inside it goes
 * with it, so the dialog says so when the place is not empty.
 *
 * @param {Object} props
 * @param {import('../../utils/storageTree').StorageNode} props.node
 * @param {number} [props.childCount]
 * @param {Function} props.onRemove - (token, nodeId) => Promise<{ success, toast }|string[]>
 * @param {Function} [props.onDeleted] - (node) => void
 * @param {string} [props.className]
 */
export const StorageNodeRemoveButton = ({
  node,
  childCount = 0,
  onRemove,
  onDeleted,
  className = 'storage-node__remove nodrag nopan',
}) => (
  <div
    className={className}
    onMouseDown={e => e.stopPropagation()}
    onClick={e => e.stopPropagation()}
  >
    <DeleteButton
      objectType="storage place"
      objectName={node?.name}
      objectId={node?.id}
      extraMessage={
        childCount > 0
          ? 'Everything inside it is removed too. Lots in those places are unplaced, not deleted.'
          : undefined
      }
      deleteFetchFunc={onRemove}
      onSuccessfulDelete={() => onDeleted?.(node)}
    />
  </div>
);

/**
 * Everything a place can do, shared by the map card and the list row so the two
 * views cannot drift apart: Place item, Open chart, Edit, Move, Rename, and
 * Add child. Each form opens here, in place of the buttons it replaces.
 *
 * Clicks stop here so the surface underneath (a draggable card, a clickable
 * row) does not also react to them.
 *
 * @param {Object} props
 * @param {import('../../utils/storageTree').StorageNode} props.node
 * @param {Array} [props.nodes] - Full tree; Move needs it to list destinations
 * @param {Function} [props.onAdd] - (parent, payload) => boolean|Promise
 * @param {Function} [props.onOpenBox] - (node) => void. Slotted boxes only
 * @param {Function} [props.onEditBox] - (node, { name, slotted, slotCount }) => boolean|Promise
 * @param {Function} [props.onMove] - (node, parentId) => boolean|Promise
 * @param {Function} [props.onRename] - (node, name) => boolean|Promise
 * @param {Function} [props.onPlaceItem] - (node) => void
 * @param {Function} [props.onFormOpenChange] - (formOpen) => void. The card uses it to
 *   grow and push the cards under it down; the row uses it to take the full width
 * @param {string} [props.buttonClassName]
 * @param {string} [props.addButtonClassName]
 * @param {string} [props.addFormIdPrefix] - Keeps form labels unique per view
 * @param {boolean} [props.showOpenChart] - False where the view already has its own
 *   Open control for a slotted box (the columns toolbar)
 */
const StorageNodeActions = ({
  node,
  nodes = [],
  onAdd,
  onOpenBox,
  onEditBox,
  onMove,
  onRename,
  onPlaceItem,
  onFormOpenChange,
  buttonClassName = 'storage-node__chart nodrag nopan',
  addButtonClassName = 'storage-node__add nodrag nopan',
  addFormIdPrefix,
  showOpenChart = true,
}) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const formOpen = adding || editing || moving || renaming;

  const openable = canHaveChildren(node);
  const slotCount = readSlotCount(node);
  const isBox = node?.kind === 'box';
  const canPlaceItem = Boolean(node && typeof onPlaceItem === 'function');
  const canMove = Boolean(
    node?.kind && node.kind !== 'room' && typeof onMove === 'function'
  );
  const canRename = typeof onRename === 'function';
  const canEditBox = typeof onEditBox === 'function';
  const canAdd = typeof onAdd === 'function';

  useEffect(() => {
    onFormOpenChange?.(formOpen);
  }, [formOpen, onFormOpenChange]);

  const childKindLabel =
    node?.kind === 'room'
      ? 'storage unit'
      : node?.kind === 'unit'
        ? 'shelf, rack, or drawer'
        : 'box';

  return (
    <>
      {renaming ? (
        <StorageRenameForm
          node={node}
          onSave={async name => {
            const ok = await onRename?.(node, name);
            if (ok !== false) setRenaming(false);
            return ok;
          }}
          onCancel={() => setRenaming(false)}
        />
      ) : moving ? (
        <StorageMoveForm
          node={node}
          nodes={nodes}
          onSave={async parentId => {
            const ok = await onMove?.(node, parentId);
            if (ok !== false) setMoving(false);
            return ok;
          }}
          onCancel={() => setMoving(false)}
        />
      ) : isBox && editing ? (
        <StorageBoxEditForm
          box={node}
          onSave={async payload => {
            const ok = await onEditBox?.(node, payload);
            if (ok !== false) setEditing(false);
            return ok;
          }}
          onCancel={() => setEditing(false)}
        />
      ) : isBox ? (
        <>
          {slotCount && showOpenChart ? (
            <Button
              variant="outline-primary"
              size="sm"
              className={buttonClassName}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation();
                onOpenBox?.(node);
              }}
            >
              Open chart
            </Button>
          ) : null}
          {canPlaceItem ? (
            <Button
              variant="outline-primary"
              size="sm"
              className={buttonClassName}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation();
                onPlaceItem?.(node);
              }}
            >
              Place item
            </Button>
          ) : null}
          {canEditBox ? (
          <Button
            variant="outline-secondary"
            size="sm"
            className={buttonClassName}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              setEditing(true);
            }}
          >
            Edit
          </Button>
          ) : null}
          {canMove ? (
            <Button
              variant="outline-secondary"
              size="sm"
              className={buttonClassName}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation();
                setAdding(false);
                setMoving(true);
              }}
            >
              Move
            </Button>
          ) : null}
        </>
      ) : null}

      {canPlaceItem && !isBox && !renaming ? (
        <Button
          variant="outline-primary"
          size="sm"
          className={buttonClassName}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            onPlaceItem?.(node);
          }}
        >
          Place item
        </Button>
      ) : null}

      {canMove && !isBox && !moving && !renaming ? (
        <Button
          variant="outline-secondary"
          size="sm"
          className={buttonClassName}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            setAdding(false);
            setMoving(true);
          }}
        >
          Move
        </Button>
      ) : null}

      {canRename && !renaming && !moving && !editing && !adding ? (
        <Button
          variant="outline-secondary"
          size="sm"
          className={buttonClassName}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            setAdding(false);
            setRenaming(true);
          }}
        >
          Rename
        </Button>
      ) : null}

      {canAdd && openable && !moving && !renaming ? (
        adding ? (
          <>
            <StorageAddForm
              parent={node}
              compact
              idPrefix={addFormIdPrefix || `node-${node?.id}`}
              onAdd={async payload => {
                const ok = await onAdd?.(node, payload);
                if (ok !== false) setAdding(false);
              }}
            />
            <Button
              variant="link"
              size="sm"
              className="storage-node__cancel nodrag nopan p-0"
              onMouseDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation();
                setAdding(false);
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            variant="outline-success"
            size="sm"
            className={addButtonClassName}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              setAdding(true);
            }}
          >
            + Add {childKindLabel}
          </Button>
        )
      ) : null}
    </>
  );
};

export default StorageNodeActions;
